import { useState, useMemo, useRef, useEffect } from 'react';
import { Plus, X, Trash2, Search, ChevronLeft, BarChart3, Globe, Lock, Star, Copy, Share2, Upload, Trash, Sparkles, History, Rows3, Grid3x3, Hand, GitCompare, StickyNote, ArrowLeftRight, Layers } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { RarityBadge } from '@/components/RarityBadge';
import { LegalityPanel } from '@/components/LegalityPanel';
import { DeckSuggestions } from '@/components/DeckSuggestions';
import { DeckExportSheet } from '@/components/DeckExportSheet';
import { DeckImportSheet } from '@/components/DeckImportSheet';
import { DeckHistorySheet } from '@/components/DeckHistorySheet';
import { DeckSuggestSheet } from '@/components/DeckSuggestSheet';
import { PlaytestSheet } from '@/components/PlaytestSheet';
import { CompareSnapshotsSheet } from '@/components/CompareSnapshotsSheet';
import { useDecks, useDeckCards, useCreateDeck, useDeleteDeck, useAddCardToDeck, useRemoveCardFromDeck, useSetDeckCardQty, useToggleSideboard, useUpdateDeckCardNotes } from '@/hooks/useDecks';
import { useCreateDeckSnapshot } from '@/hooks/useDeckSnapshots';
import { usePublicDecks, useTogglePublish, useRateDeck, useCopyDeck } from '@/hooks/useDeckSharing';
import { useCards, type Card } from '@/hooks/useCards';
import { useAuth } from '@/hooks/useAuth';
import { validateDeck, buildLegalityFixes, type LegalityFix } from '@/lib/deck-utils';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';
import { PersonalizedSuggestions } from '@/components/PersonalizedSuggestions';


const INK_OPTIONS: InkColor[] = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];

export function DeckBuilderTab() {
  const { user } = useAuth();
  const { data: decks, isLoading } = useDecks();
  const createDeck = useCreateDeck();
  const deleteDeck = useDeleteDeck();
  const togglePublish = useTogglePublish();

  const [viewMode, setViewMode] = useState<'my' | 'public'>('my');
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newInk1, setNewInk1] = useState<InkColor>('Amber');
  const [newInk2, setNewInk2] = useState<InkColor>('Amethyst');

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const deck = await createDeck.mutateAsync({ name: newName, ink_1: newInk1, ink_2: newInk2 });
      toast.success('Deck created!');
      setShowCreate(false);
      setNewName('');
      setActiveDeckId(deck.id);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDeck.mutateAsync(id);
      toast.success('Deck deleted');
      if (activeDeckId === id) setActiveDeckId(null);
    } catch (err: any) { toast.error(err.message); }
  };

  if (activeDeckId) {
    return <DeckEditor deckId={activeDeckId} onBack={() => setActiveDeckId(null)} />;
  }

  if (viewMode === 'public') {
    return <PublicDeckGallery onSwitchToMy={() => setViewMode('my')} />;
  }

  return (
    <div className="px-4 pb-4">
      <PersonalizedSuggestions surface="decks" />
      {/* View toggle */}
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mt-3 mb-3">
        <button className="flex-1 rounded-lg py-2 text-xs font-medium font-body bg-card text-foreground shadow-sm">My Decks</button>
        <button onClick={() => setViewMode('public')} className="flex-1 rounded-lg py-2 text-xs font-medium font-body text-muted-foreground">Public Decks</button>
      </div>

      {/* Create button */}
      <button
        onClick={() => setShowCreate(true)}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 py-4 text-sm font-medium text-primary font-body hover:border-primary/60 transition-colors"
      >
        <Plus className="h-5 w-5" /> New Deck
      </button>

      {/* Deck list */}
      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">Loading...</div>
      ) : !decks?.length ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3">🃏</p>
          <p className="text-sm font-medium text-foreground font-body mb-1">No decks yet</p>
          <p className="text-xs text-muted-foreground font-body">Create your first 60-card deck to start building!</p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {decks.map(deck => (
            <div key={deck.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-sm font-semibold text-foreground">{deck.name}</h3>
                <button onClick={() => handleDelete(deck.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <InkDot ink={deck.ink_1 as InkColor} size="md" />
                <InkDot ink={deck.ink_2 as InkColor} size="md" />
                <span className="text-xs text-muted-foreground font-body">{deck.ink_1} / {deck.ink_2}</span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${deck.total_cards === 60 ? 'bg-ink-emerald/20 text-ink-emerald' : 'bg-secondary text-muted-foreground'}`}>
                  {deck.total_cards}/60 cards
                </span>
              </div>
              {deck.description && <p className="text-xs text-muted-foreground font-body mb-3">{deck.description}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveDeckId(deck.id)}
                  className="flex-1 rounded-lg bg-primary py-2.5 text-xs font-medium text-primary-foreground font-body"
                >
                  Edit Deck
                </button>
                <button
                  onClick={async () => {
                    try {
                      await togglePublish.mutateAsync({ deckId: deck.id, published: !(deck as any).published });
                      toast.success((deck as any).published ? 'Deck unpublished' : 'Deck published!');
                    } catch (err: any) { toast.error(err.message); }
                  }}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2.5 text-xs font-medium font-body transition-colors ${(deck as any).published ? 'bg-ink-emerald/10 text-ink-emerald border border-ink-emerald/30' : 'bg-secondary text-muted-foreground'}`}
                >
                  {(deck as any).published ? <><Globe className="h-3 w-3" /> Public</> : <><Lock className="h-3 w-3" /> Private</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="animate-slide-up w-full max-w-[430px] rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-foreground">New Deck</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Deck name" className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary mb-3" />
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Ink 1</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {INK_OPTIONS.map(ink => (
                <button key={ink} onClick={() => setNewInk1(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${newInk1 === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  <InkDot ink={ink} size="sm" />{ink}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Ink 2</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {INK_OPTIONS.map(ink => (
                <button key={ink} onClick={() => setNewInk2(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${newInk2 === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  <InkDot ink={ink} size="sm" />{ink}
                </button>
              ))}
            </div>
            <button onClick={handleCreate} disabled={!newName.trim() || createDeck.isPending} className="w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50">
              {createDeck.isPending ? 'Creating...' : 'Create Deck'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deck Editor ────────────────────────────────────────────
function DeckEditor({ deckId, onBack }: { deckId: string; onBack: () => void }) {
  const { data: deckCards, isLoading } = useDeckCards(deckId);
  const { data: decks } = useDecks();
  const addCard = useAddCardToDeck();
  const removeCard = useRemoveCardFromDeck();
  const setQty = useSetDeckCardQty();
  const toggleSideboard = useToggleSideboard();
  const updateNotes = useUpdateDeckCardNotes();
  const createSnapshot = useCreateDeckSnapshot();
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showPlaytest, setShowPlaytest] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [layout, setLayout] = useState<'list' | 'curve'>('list');
  const [expandedNotes, setExpandedNotes] = useState<string | null>(null);

  const deck = decks?.find(d => d.id === deckId);
  const allCards = deckCards || [];
  const cards = useMemo(() => allCards.filter(c => !c.sideboard), [allCards]);
  const sideboardCards = useMemo(() => allCards.filter(c => c.sideboard), [allCards]);
  const totalCards = cards.reduce((sum, c) => sum + c.quantity, 0);
  const sideboardCount = sideboardCards.reduce((s, c) => s + c.quantity, 0);

  const legality = useMemo(() => {
    if (!deck) return null;
    return validateDeck(cards, [deck.ink_1, deck.ink_2]);
  }, [cards, deck]);

  const legalityFixes = useMemo(() => {
    if (!legality) return {};
    return buildLegalityFixes(legality, cards);
  }, [legality, cards]);


  const cardIds = useMemo(() => cards.map(c => c.card_id), [cards]);

  // Ink curve analysis
  const costCurve = useMemo(() => {
    const curve: Record<number, number> = {};
    cards.forEach(dc => {
      const cost = dc.card?.cost ?? 0;
      const bucket = cost >= 7 ? 7 : cost;
      curve[bucket] = (curve[bucket] || 0) + dc.quantity;
    });
    return curve;
  }, [cards]);

  const maxCurve = Math.max(...Object.values(costCurve), 1);

  // Type breakdown
  const typeBreakdown = useMemo(() => {
    const types: Record<string, number> = {};
    cards.forEach(dc => {
      const t = dc.card?.card_type || 'Unknown';
      types[t] = (types[t] || 0) + dc.quantity;
    });
    return types;
  }, [cards]);

  // Ink breakdown
  const inkBreakdown = useMemo(() => {
    const inks: Record<string, number> = {};
    cards.forEach(dc => {
      const ink = dc.card?.ink_color || 'Unknown';
      inks[ink] = (inks[ink] || 0) + dc.quantity;
    });
    return inks;
  }, [cards]);

  const inkableCount = cards.reduce((sum, dc) => sum + (dc.card?.inkable ? dc.quantity : 0), 0);

  // Average cost (cards with cost only) and total deck lore potential.
  const { avgCost, totalLore } = useMemo(() => {
    let costSum = 0;
    let costN = 0;
    let lore = 0;
    for (const dc of cards) {
      const c = dc.card;
      if (!c) continue;
      if (typeof c.cost === 'number') { costSum += c.cost * dc.quantity; costN += dc.quantity; }
      if (typeof c.lore_value === 'number') lore += c.lore_value * dc.quantity;
    }
    return { avgCost: costN ? costSum / costN : 0, totalLore: lore };
  }, [cards]);

  const handleAdd = async (card: Card) => {
    try {
      await addCard.mutateAsync({ deckId, cardId: card.id });
      toast.success(`Added ${card.name}`);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRemove = async (cardId: string) => {
    try {
      await removeCard.mutateAsync({ deckId, cardId });
    } catch (err: any) { toast.error(err.message); }
  };

  const handleSetQty = async (cardId: string, quantity: number) => {
    try {
      await setQty.mutateAsync({ deckId, cardId, quantity });
    } catch (err: any) { toast.error(err.message); }
  };

  const handleClear = async (cardId: string) => {
    try {
      await removeCard.mutateAsync({ deckId, cardId, removeAll: true });
    } catch (err: any) { toast.error(err.message); }
  };

  const handleApplyFix = async (fix: LegalityFix) => {
    try {
      if (fix.action === 'trim-to-4') {
        await setQty.mutateAsync({ deckId, cardId: fix.cardId, quantity: 4 });
        toast.success(`Trimmed ${fix.cardName} to 4`);
      } else if (fix.action === 'remove-all') {
        await removeCard.mutateAsync({ deckId, cardId: fix.cardId, removeAll: true });
        toast.success(`Removed ${fix.cardName}`);
      } else {
        await removeCard.mutateAsync({ deckId, cardId: fix.cardId });
        toast.success(`Removed 1 ${fix.cardName}`);
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // Auto-snapshot before destructive bulk operations.
  const snapshotBefore = async (label: string) => {
    if (!cards.length) return;
    try {
      await createSnapshot.mutateAsync({ deckId, label, cards });
    } catch {
      /* non-fatal */
    }
  };

  return (
    <div className="px-4 pb-4">
      {/* Header */}
      <div className="mt-3 flex items-center gap-2 mb-3">
        <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-foreground" aria-label="Back">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-base font-semibold text-foreground truncate">{deck?.name || 'Deck'}</h2>
          <div className="flex items-center gap-2">
            {deck && <InkDot ink={deck.ink_1 as InkColor} size="sm" />}
            {deck && <InkDot ink={deck.ink_2 as InkColor} size="sm" />}
            <span className={`text-xs font-body ${totalCards === 60 ? 'text-ink-emerald' : totalCards > 60 ? 'text-ink-ruby' : 'text-muted-foreground'}`}>
              {totalCards}/60 cards
            </span>
          </div>
        </div>
        <button onClick={() => setShowCardPicker(true)} className="flex h-8 items-center gap-1 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground font-body">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-1.5 mb-3 overflow-x-auto -mx-1 px-1 pb-1">
        <button
          onClick={() => setShowSuggest(true)}
          disabled={!cards.length}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 h-8 text-[11px] font-medium text-primary font-body hover:bg-primary/20 transition-colors disabled:opacity-40"
        >
          <Sparkles className="h-3.5 w-3.5" /> Ask Scout
        </button>
        <button
          onClick={() => setShowStats(!showStats)}
          className={`flex-shrink-0 flex items-center gap-1.5 rounded-lg px-2.5 h-8 text-[11px] font-medium font-body transition-colors ${showStats ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
          aria-label="Toggle stats"
        >
          <BarChart3 className="h-3.5 w-3.5" /> Stats
        </button>
        <button
          onClick={() => setLayout(l => (l === 'list' ? 'curve' : 'list'))}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 h-8 text-[11px] font-medium text-muted-foreground hover:text-foreground font-body"
          aria-label="Toggle layout"
        >
          {layout === 'list' ? <><Grid3x3 className="h-3.5 w-3.5" /> Curve view</> : <><Rows3 className="h-3.5 w-3.5" /> List view</>}
        </button>
        <button
          onClick={() => setShowPlaytest(true)}
          disabled={cards.length < 7}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 h-8 text-[11px] font-medium text-muted-foreground hover:text-foreground font-body disabled:opacity-40"
          aria-label="Playtest hand"
        >
          <Hand className="h-3.5 w-3.5" /> Playtest
        </button>
        <button
          onClick={() => setShowHistory(true)}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 h-8 text-[11px] font-medium text-muted-foreground hover:text-foreground font-body"
          aria-label="Deck history"
        >
          <History className="h-3.5 w-3.5" /> History
        </button>
        <button
          onClick={() => setShowCompare(true)}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 h-8 text-[11px] font-medium text-muted-foreground hover:text-foreground font-body"
          aria-label="Compare snapshots"
        >
          <GitCompare className="h-3.5 w-3.5" /> Compare
        </button>
        <button
          onClick={async () => {
            await snapshotBefore('Before import');
            setShowImport(true);
          }}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 h-8 text-[11px] font-medium text-muted-foreground hover:text-foreground font-body"
          aria-label="Import deck list"
        >
          <Upload className="h-3.5 w-3.5" /> Import
        </button>
        <button
          onClick={() => setShowExport(true)}
          disabled={!cards.length}
          className="flex-shrink-0 flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 h-8 text-[11px] font-medium text-muted-foreground hover:text-foreground font-body disabled:opacity-40"
          aria-label="Export deck"
        >
          <Share2 className="h-3.5 w-3.5" /> Export
        </button>
      </div>

      {/* Legality validator */}
      {legality && cards.length > 0 && (
        <LegalityPanel report={legality} fixes={legalityFixes} onApplyFix={handleApplyFix} />
      )}


      {/* Suggestions */}
      {deck && cards.length > 0 && (
        <DeckSuggestions
          deckId={deckId}
          currentCardIds={cardIds}
          allowedInks={[deck.ink_1 as InkColor, deck.ink_2 as InkColor]}
          onAdd={handleAdd}
        />
      )}

      {/* Stats panel */}
      {showStats && (
        <div className="rounded-xl border border-border bg-card p-4 mb-3 animate-fade-in">
          <h3 className="text-xs font-medium text-foreground font-body mb-3">Ink Curve</h3>
          <div className="flex items-end gap-1 h-20 mb-1">
            {[0, 1, 2, 3, 4, 5, 6, 7].map(cost => {
              const count = costCurve[cost] || 0;
              const height = maxCurve > 0 ? (count / maxCurve) * 100 : 0;
              return (
                <div key={cost} className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[9px] text-muted-foreground font-body">{count || ''}</span>
                  <div className="w-full rounded-t bg-primary/60" style={{ height: `${height}%`, minHeight: count > 0 ? '4px' : '0' }} />
                </div>
              );
            })}
          </div>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4, 5, 6, '7+'].map(c => (
              <span key={c} className="flex-1 text-center text-[9px] text-muted-foreground font-body">{c}</span>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Type</p>
              {Object.entries(typeBreakdown).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between text-xs font-body py-0.5">
                  <span className="text-foreground">{type}</span>
                  <span className="text-muted-foreground">{count}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Ink Split</p>
              {Object.entries(inkBreakdown).map(([ink, count]) => (
                <div key={ink} className="flex items-center gap-1.5 text-xs font-body py-0.5">
                  <InkDot ink={ink as InkColor} size="sm" />
                  <span className="text-foreground">{ink}</span>
                  <span className="ml-auto text-muted-foreground">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 space-y-1.5 text-xs font-body">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Inkable cards</span>
              <span className={`font-medium ${inkableCount >= 20 ? 'text-ink-emerald' : 'text-ink-ruby'}`}>{inkableCount}/{totalCards}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Average cost</span>
              <span className="font-medium text-foreground">{avgCost.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total lore potential</span>
              <span className="font-medium text-primary">{totalLore}</span>
            </div>
          </div>
        </div>
      )}

      {/* Card list */}
      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">Loading...</div>
      ) : cards.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3">📝</p>
          <p className="text-sm text-muted-foreground font-body">Tap "Add" to start building your deck</p>
        </div>
      ) : layout === 'curve' ? (
        <CurveLayout
          cards={cards}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onClear={handleClear}
        />
      ) : (
        <div className="space-y-4">
          <DeckCardList
            title="Main deck"
            cards={cards}
            expandedNotes={expandedNotes}
            setExpandedNotes={setExpandedNotes}
            handleAdd={handleAdd}
            handleRemove={handleRemove}
            handleSetQty={handleSetQty}
            handleClear={handleClear}
            handleToggleSideboard={(dc) =>
              toggleSideboard.mutate({ deckCardId: dc.id, sideboard: true, deckId })
            }
            handleSaveNotes={(dc, notes) =>
              updateNotes.mutate({ deckCardId: dc.id, notes: notes.trim() ? notes : null, deckId })
            }
            moveLabel="Move to sideboard"
          />
          {sideboardCards.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5 px-0.5">
                <Layers className="h-3.5 w-3.5 text-primary" />
                <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-body">
                  Sideboard ({sideboardCount})
                </h4>
              </div>
              <DeckCardList
                title="Sideboard"
                cards={sideboardCards}
                expandedNotes={expandedNotes}
                setExpandedNotes={setExpandedNotes}
                handleAdd={handleAdd}
                handleRemove={handleRemove}
                handleSetQty={handleSetQty}
                handleClear={handleClear}
                handleToggleSideboard={(dc) =>
                  toggleSideboard.mutate({ deckCardId: dc.id, sideboard: false, deckId })
                }
                handleSaveNotes={(dc, notes) =>
                  updateNotes.mutate({ deckCardId: dc.id, notes: notes.trim() ? notes : null, deckId })
                }
                moveLabel="Move to main deck"
                subdued
              />
            </div>
          )}
        </div>
      )}

      {/* Card picker modal */}
      {showCardPicker && deck && (
        <CardPicker
          ink1={deck.ink_1 as InkColor}
          ink2={deck.ink_2 as InkColor}
          onSelect={handleAdd}
          onQuickPlayset={(card) => {
            handleSetQty(card.id, 4);
            toast.success(`Added 4× ${card.name}`);
          }}
          onClose={() => setShowCardPicker(false)}
          existingCards={allCards}
        />
      )}

      {/* Export sheet */}
      {deck && (
        <DeckExportSheet
          open={showExport}
          onClose={() => setShowExport(false)}
          deckName={deck.name}
          ink1={deck.ink_1 as InkColor}
          ink2={deck.ink_2 as InkColor}
          cards={cards}
        />
      )}

      {/* Import sheet */}
      {deck && (
        <DeckImportSheet
          open={showImport}
          onClose={() => setShowImport(false)}
          deckId={deckId}
          allowedInks={[deck.ink_1 as InkColor, deck.ink_2 as InkColor]}
        />
      )}

      {/* History sheet */}
      <DeckHistorySheet
        open={showHistory}
        onClose={() => setShowHistory(false)}
        deckId={deckId}
        cards={cards}
      />

      {/* Playtest */}
      <PlaytestSheet
        open={showPlaytest}
        onClose={() => setShowPlaytest(false)}
        cards={cards}
        deckName={deck?.name}
      />

      {/* Compare snapshots */}
      <CompareSnapshotsSheet
        open={showCompare}
        onClose={() => setShowCompare(false)}
        deckId={deckId}
        currentCards={cards}
      />

      {/* Scout suggestions */}
      {deck && (
        <DeckSuggestSheet
          open={showSuggest}
          onClose={() => setShowSuggest(false)}
          deckName={deck.name}
          ink1={deck.ink_1 as InkColor}
          ink2={deck.ink_2 as InkColor}
          cards={cards}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
}

// ─── Curve Layout ───────────────────────────────────────────
function CurveLayout({
  cards,
  onAdd,
  onRemove,
  onClear,
}: {
  cards: import('@/hooks/useDecks').DeckCard[];
  onAdd: (card: Card) => void;
  onRemove: (cardId: string) => void;
  onClear: (cardId: string) => void;
}) {
  const buckets = useMemo(() => {
    const map: Record<number, typeof cards> = {};
    for (const dc of cards) {
      if (!dc.card) continue;
      const cost = dc.card.cost ?? 0;
      const b = cost >= 7 ? 7 : cost;
      if (!map[b]) map[b] = [];
      map[b].push(dc);
    }
    for (const k of Object.keys(map)) {
      map[+k].sort((a, b) => (a.card?.name || '').localeCompare(b.card?.name || ''));
    }
    return map;
  }, [cards]);

  const costs = [0, 1, 2, 3, 4, 5, 6, 7].filter(c => buckets[c]?.length);

  return (
    <div className="space-y-3">
      {costs.map(cost => {
        const bucket = buckets[cost];
        const count = bucket.reduce((s, dc) => s + dc.quantity, 0);
        return (
          <div key={cost} className="rounded-xl border border-border bg-card p-2.5 animate-fade-in">
            <div className="flex items-center justify-between mb-2 px-0.5">
              <span className="text-xs font-medium text-foreground font-body">
                ⬡ Cost {cost}{cost === 7 ? '+' : ''}
              </span>
              <span className="text-[10px] text-muted-foreground font-body">
                {count} card{count === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {bucket.map(dc => {
                if (!dc.card) return null;
                return (
                  <div key={dc.id} className="flex items-center gap-1.5 rounded-lg bg-secondary/50 p-1.5">
                    <CardImage
                      src={dc.card.image_url}
                      alt={dc.card.name}
                      inkColor={dc.card.ink_color as InkColor}
                      className="w-8 aspect-[2/3] rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-0.5">
                        <InkDot ink={dc.card.ink_color as InkColor} size="sm" />
                        <span className="text-[10px] font-medium text-foreground font-body truncate">
                          {dc.card.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <button
                          onClick={() => onRemove(dc.card_id)}
                          className="flex h-5 w-5 items-center justify-center rounded bg-card text-foreground text-[10px]"
                          aria-label="Remove one"
                        >
                          −
                        </button>
                        <span className="text-[10px] font-medium text-foreground font-body w-3 text-center">
                          {dc.quantity}
                        </span>
                        <button
                          onClick={() => onAdd(dc.card!)}
                          disabled={dc.quantity >= 4}
                          className="flex h-5 w-5 items-center justify-center rounded bg-card text-foreground text-[10px] disabled:opacity-30"
                          aria-label="Add one"
                        >
                          +
                        </button>
                        <button
                          onClick={() => onClear(dc.card_id)}
                          className="ml-auto flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-ink-ruby"
                          aria-label="Remove all"
                        >
                          <Trash className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─── Deck Card List (main / sideboard) ──────────────────────
function DeckCardList({
  cards,
  expandedNotes,
  setExpandedNotes,
  handleAdd,
  handleRemove,
  handleSetQty,
  handleClear,
  handleToggleSideboard,
  handleSaveNotes,
  moveLabel,
  subdued,
}: {
  title: string;
  cards: import('@/hooks/useDecks').DeckCard[];
  expandedNotes: string | null;
  setExpandedNotes: (id: string | null) => void;
  handleAdd: (card: Card) => void;
  handleRemove: (cardId: string) => void;
  handleSetQty: (cardId: string, qty: number) => void;
  handleClear: (cardId: string) => void;
  handleToggleSideboard: (dc: import('@/hooks/useDecks').DeckCard) => void;
  handleSaveNotes: (dc: import('@/hooks/useDecks').DeckCard, notes: string) => void;
  moveLabel: string;
  subdued?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      {cards.map(dc => {
        if (!dc.card) return null;
        const isOpen = expandedNotes === dc.id;
        return (
          <div key={dc.id} className={`rounded-xl border border-border ${subdued ? 'bg-card/60' : 'bg-card'} p-2 animate-fade-in`}>
            <div className="flex items-center gap-2.5">
              <CardImage src={dc.card.image_url} alt={dc.card.name} inkColor={dc.card.ink_color as InkColor} className="w-10 aspect-[2/3] rounded flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <InkDot ink={dc.card.ink_color as InkColor} size="sm" />
                  <span className="text-xs font-medium text-foreground font-body truncate">{dc.card.name}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[9px] text-muted-foreground font-body">⬡{dc.card.cost || 0}</span>
                  <RarityBadge rarity={dc.card.rarity} />
                  {dc.notes && !isOpen && (
                    <span className="text-[9px] text-primary/80 font-body italic truncate max-w-[120px]">"{dc.notes}"</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleRemove(dc.card_id)} className="flex h-6 w-6 items-center justify-center rounded bg-secondary text-foreground text-xs hover:bg-accent" aria-label="Remove one">−</button>
                <span className="text-xs font-medium text-foreground font-body w-4 text-center">{dc.quantity}</span>
                <button onClick={() => handleAdd(dc.card!)} disabled={dc.quantity >= 4} className="flex h-6 w-6 items-center justify-center rounded bg-secondary text-foreground text-xs hover:bg-accent disabled:opacity-30" aria-label="Add one">+</button>
                <button
                  onClick={() => setExpandedNotes(isOpen ? null : dc.id)}
                  className={`ml-1 flex h-6 w-6 items-center justify-center rounded ${isOpen || dc.notes ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
                  aria-label="Card notes"
                >
                  <StickyNote className="h-3 w-3" />
                </button>
                <button
                  onClick={() => handleToggleSideboard(dc)}
                  className="flex h-6 w-6 items-center justify-center rounded bg-secondary text-muted-foreground hover:text-primary"
                  aria-label={moveLabel}
                  title={moveLabel}
                >
                  <ArrowLeftRight className="h-3 w-3" />
                </button>
                <button onClick={() => handleClear(dc.card_id)} className="flex h-6 w-6 items-center justify-center rounded bg-secondary text-muted-foreground text-xs hover:text-ink-ruby" aria-label="Remove all copies">
                  <Trash className="h-3 w-3" />
                </button>
              </div>
            </div>
            {isOpen && (
              <NotesEditor
                initial={dc.notes ?? ''}
                onSave={(v) => { handleSaveNotes(dc, v); setExpandedNotes(null); }}
                onCancel={() => setExpandedNotes(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function NotesEditor({ initial, onSave, onCancel }: { initial: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  return (
    <div className="mt-2 pt-2 border-t border-border">
      <textarea
        value={value}
        onChange={e => setValue(e.target.value.slice(0, 240))}
        placeholder="Sideboard plan, mulligan tips, matchup notes…"
        rows={2}
        className="w-full rounded-lg border border-border bg-secondary px-2.5 py-2 text-xs text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
      />
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground font-body">{value.length}/240</span>
        <div className="flex gap-1.5">
          <button onClick={onCancel} className="text-[11px] text-muted-foreground hover:text-foreground font-body px-2 py-1">Cancel</button>
          <button onClick={() => onSave(value)} className="rounded bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground font-body">Save note</button>
        </div>
      </div>
    </div>
  );
}

function LongPressAddRow({ card, qty, onTap, onLongPress }: {
  card: Card;
  qty: number;
  onTap: () => void;
  onLongPress: () => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);
  const [holding, setHolding] = useState(false);

  const clear = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setHolding(false);
  };

  useEffect(() => () => clear(), []);

  const start = () => {
    if (qty >= 4) return;
    firedRef.current = false;
    setHolding(true);
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      setHolding(false);
      try { navigator.vibrate?.(30); } catch { /* ignore */ }
      onLongPress();
    }, 450);
  };

  const end = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!firedRef.current) onTap();
    setHolding(false);
  };

  const cancel = () => clear();

  return (
    <button
      type="button"
      onPointerDown={start}
      onPointerUp={end}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
      disabled={qty >= 4}
      className={`flex w-full items-center gap-2.5 rounded-xl border ${holding ? 'border-primary bg-primary/10' : 'border-border bg-secondary'} p-2 text-left hover:border-primary/30 transition-colors disabled:opacity-40 select-none touch-none`}
    >
      <CardImage src={card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} className="w-8 aspect-[2/3] rounded flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs font-medium text-foreground font-body truncate block">{card.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-muted-foreground font-body">⬡{card.cost || 0}</span>
          <RarityBadge rarity={card.rarity} />
          {holding && <span className="text-[9px] text-primary font-body font-medium animate-pulse">Hold for ×4…</span>}
        </div>
      </div>
      {qty > 0 && <span className="text-[10px] text-primary font-body font-medium">×{qty}</span>}
      <Plus className="h-4 w-4 text-primary flex-shrink-0" />
    </button>
  );
}




function CardPicker({
  ink1, ink2, onSelect, onQuickPlayset, onClose, existingCards,
}: {
  ink1: InkColor; ink2: InkColor;
  onSelect: (card: Card) => void;
  onQuickPlayset: (card: Card) => void;
  onClose: () => void;
  existingCards: { card_id: string; quantity: number }[];
}) {
  const [search, setSearch] = useState('');
  const [inkFilter, setInkFilter] = useState<'both' | InkColor>('both');
  const [costFilter, setCostFilter] = useState<number | 'all'>('all');

  // Fetch the full catalog once — filtering happens locally so users can flip
  // between inks and costs without a round trip per change.
  const { data: allCards, isLoading } = useCards({ search: search || undefined });

  const allowedInks: InkColor[] = [ink1, ink2];
  const cards = useMemo(() => {
    const base = (allCards || []).filter(c => allowedInks.includes(c.ink_color as InkColor));
    return base.filter(c => {
      if (inkFilter !== 'both' && c.ink_color !== inkFilter) return false;
      if (costFilter !== 'all') {
        const cost = c.cost ?? 0;
        if (costFilter === 7) { if (cost < 7) return false; }
        else if (cost !== costFilter) return false;
      }
      return true;
    });
  }, [allCards, inkFilter, costFilter, ink1, ink2]);

  const existingMap = new Map(existingCards.map(c => [c.card_id, c.quantity]));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] max-h-[85vh] rounded-t-2xl border border-border bg-card flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-semibold text-foreground">Add Cards</h3>
            <button onClick={onClose} className="text-muted-foreground" aria-label="Close picker"><X className="h-5 w-5" /></button>
          </div>
          {/* Ink toggle — Both / Ink1 / Ink2 */}
          <div className="flex gap-1.5 mb-2">
            <button onClick={() => setInkFilter('both')} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-medium font-body transition-colors ${inkFilter === 'both' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
              Both
            </button>
            {[ink1, ink2].map(ink => (
              <button key={ink} onClick={() => setInkFilter(ink)} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-medium font-body transition-colors ${inkFilter === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                <InkDot ink={ink} size="sm" />{ink}
              </button>
            ))}
          </div>
          {/* Cost filter */}
          <div className="flex gap-1 mb-3 overflow-x-auto -mx-1 px-1 pb-1">
            <button onClick={() => setCostFilter('all')} className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${costFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
              Any cost
            </button>
            {[0,1,2,3,4,5,6,7].map(c => (
              <button key={c} onClick={() => setCostFilter(c)} className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${costFilter === c ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                ⬡{c}{c === 7 ? '+' : ''}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cards..." className="w-full rounded-lg border border-border bg-secondary py-2 pl-9 pr-3 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground font-body">{cards.length} match{cards.length === 1 ? '' : 'es'}</p>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground font-body text-sm">Loading...</div>
          ) : (
            <>
              <p className="text-[10px] text-muted-foreground font-body mb-2 flex items-center gap-1">
                <Hand className="h-3 w-3" /> Tap to add 1 · long-press for a full playset (×4)
              </p>
              <div className="space-y-1.5">
                {cards.map(card => {
                  const qty = existingMap.get(card.id) || 0;
                  return (
                    <LongPressAddRow
                      key={card.id}
                      card={card}
                      qty={qty}
                      onTap={() => onSelect(card)}
                      onLongPress={() => onQuickPlayset(card)}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Public Deck Gallery ────────────────────────────────────
function PublicDeckGallery({ onSwitchToMy }: { onSwitchToMy: () => void }) {
  const { user } = useAuth();
  const { data: publicDecks, isLoading } = usePublicDecks();
  const rateDeck = useRateDeck();
  const copyDeck = useCopyDeck();

  const handleCopy = async (deckId: string) => {
    try {
      await copyDeck.mutateAsync(deckId);
      toast.success('Deck copied to your collection!');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRate = async (deckId: string, rating: number) => {
    try {
      await rateDeck.mutateAsync({ deckId, rating });
    } catch (err: any) { toast.error(err.message); }
  };

  return (
    <div className="px-4 pb-4">
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mt-3 mb-3">
        <button onClick={onSwitchToMy} className="flex-1 rounded-lg py-2 text-xs font-medium font-body text-muted-foreground">My Decks</button>
        <button className="flex-1 rounded-lg py-2 text-xs font-medium font-body bg-card text-foreground shadow-sm">Public Decks</button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">Loading...</div>
      ) : !publicDecks?.length ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3">🌐</p>
          <p className="text-sm font-medium text-foreground font-body mb-1">No public decks yet</p>
          <p className="text-xs text-muted-foreground font-body">Be the first to publish a deck!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {publicDecks.map(deck => (
            <div key={deck.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
              <div className="flex items-center gap-2 mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-sm">{deck.profile_emoji}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-sm font-semibold text-foreground truncate">{deck.name}</h3>
                  <span className="text-[10px] text-muted-foreground font-body">by {deck.profile_username}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <InkDot ink={deck.ink_1 as InkColor} size="md" />
                <InkDot ink={deck.ink_2 as InkColor} size="md" />
                <span className="text-xs text-muted-foreground font-body">{deck.ink_1} / {deck.ink_2}</span>
                <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground font-body">
                  {deck.total_cards}/60
                </span>
              </div>
              {deck.description && <p className="text-xs text-muted-foreground font-body mb-3">{deck.description}</p>}

              {/* Rating */}
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onClick={() => user && handleRate(deck.id, star)}
                    className="transition-colors"
                  >
                    <Star className={`h-4 w-4 ${(deck.user_rating || 0) >= star ? 'fill-primary text-primary' : (deck.avg_rating || 0) >= star ? 'fill-primary/30 text-primary/30' : 'text-muted-foreground/30'}`} />
                  </button>
                ))}
                <span className="text-[10px] text-muted-foreground font-body ml-1">
                  {deck.avg_rating ? deck.avg_rating.toFixed(1) : '—'} ({deck.rating_count || 0})
                </span>
              </div>

              {/* Actions */}
              {user && deck.user_id !== user.id && (
                <button
                  onClick={() => handleCopy(deck.id)}
                  disabled={copyDeck.isPending}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-primary/10 py-2.5 text-xs font-medium text-primary font-body hover:bg-primary/20 transition-colors disabled:opacity-50"
                >
                  <Copy className="h-3.5 w-3.5" /> {copyDeck.isPending ? 'Copying...' : 'Copy to My Decks'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
